import React, { useState, useEffect, useRef, useContext } from 'react';
import { useSpring, animated } from 'react-spring';
import { Typography, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import './../BottomSheet.css';
import { useDrag } from 'react-use-gesture';
import { WebSocketContext } from "../WebSocketContext";
import { useTheme } from '../contexts/ThemeContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CustomToast from './CustomToast';
import { mediatorAppFetch } from '../utils/apiFetch';
import { playTradeSound } from '../utils/tradeSound';
import ScriptIcon from './ScriptIcon';
import { displayScriptName } from '../utils/lotVariant';
import { findWatchlistPosition, planOppositeWatchlistOrder } from '../utils/watchlistNetOrder';
import { pendingOrdersCancelledNote } from '../utils/pendingOrderNotice';
import { formatLotCount, openedLotsForScript } from '../utils/openedPosition';

const BottomSheet = ({ userDetails, stock, isOpen, onClose }) => {
  const { refreshTradesAndPortfolio, submitRejectionLog, netPositions } = useContext(WebSocketContext);
  const { isDarkMode } = useTheme();
  const [disableBtn, setDisable] = useState(false);
  const styles = useSpring({
    transform: isOpen ? 'translateY(0%)' : 'translateY(100%)',
  });

  // console.log("BottomSheet",stock);

  const showToast = (type, message) => {
    if(type==1){
      toast.success(<CustomToast title={"Trade Placed"} description={message} />, {
          position:'top-right',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
      });
    }
    else if(type==2){
      toast.success(<CustomToast title={"Order Placed"} description={message} />, {
          position:'top-right',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
      });
    }
    else if(type==3){
      toast.error(<CustomToast title={"Order Failed"} description={message} />, {
          position:'top-right',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
      });
    }
};

  const [{ y }, api] = useSpring(() => ({ y: 0 }));

  const [successAlert, setSuccessAlert] = useState({ show: false, title: '', message: '' });

  const successAlertSpring = useSpring({
    opacity: successAlert.show ? 1 : 0,
    scale: successAlert.show ? 1 : 0.85,
    config: { tension: 300, friction: 25 }
  });

  const handleSuccessAlertClose = () => {
    setSuccessAlert({ show: false, title: '', message: '' });
    onClose();
  };

  React.useEffect(() => {
    api.start({ y: isOpen ? 0 : 100 });
    // console.log("userDetails",userDetails.FUTCOM_margin_type );
  }, [isOpen, api]);

  // const bind = useDrag(
  //   ({ down, movement: [, my], direction: [, dy], velocity }) => {
  //     if (down && dy > 0 && velocity > 0.2) {
  //       onClose();
  //     } else {
  //       api.start({ y: down ? my : 0 });
  //     }
  //   },
  //   { from: () => [0, y.get()] }
  // );

  const [isDepthExpanded, setIsDepthExpanded] = useState(true);
  const [lots, setLots] = useState(1);
  const [quantity, setQuantity] = useState(Math.max(stock.lotsize || 1, 1));
  const [orderType, setOrderType] = useState('market');
  const [isLimitOrder, setIsLimitOrder] = useState(false);
  const [price, setPrice] = useState('');
  const [lotVariant, setLotVariant] = useState('MEGA'); // MEGA | MINI

  const scriptKey = String(stock.scriptName || stock.dispName || stock.dispname || '')
    .trim()
    .toUpperCase();
  const miniScripts = (userDetails && userDetails.FUTCOM_mini_scripts) || {};
  const canTradeMini =
    stock.exchange === 'FUTCOM' &&
    Number(userDetails?.FUTCOM_allow_mini) === 1 &&
    !!miniScripts[scriptKey] &&
    Number(miniScripts[scriptKey].mini_lot_size) > 0;
  const effectiveLotSize =
    lotVariant === 'MINI' && canTradeMini
      ? Number(miniScripts[scriptKey].mini_lot_size)
      : Number(stock.lotsize || stock.lotSize || 1) || 1;
  const orderLotVariant = (canTradeMini && lotVariant === 'MINI') ? 'MINI' : 'MEGA';
  const openedPosition = openedLotsForScript(netPositions, stock, orderLotVariant, effectiveLotSize);
  const orderScriptLabel = (name) => (
    <>
      {displayScriptName(name) || name}
      <span className={orderLotVariant === 'MINI' ? 'lot-variant-mini' : 'lot-variant-mega'}>
        {orderLotVariant}
      </span>
    </>
  );

  const scriptWiseIntradayRate = () => {
    const raw = userDetails?.script_wise_margin?.[stock.scriptName];
    let data = raw;
    if (typeof raw === 'string') {
      try {
        data = JSON.parse(raw);
      } catch (e) {
        return 0;
      }
    }
    if (!data || typeof data !== 'object') {
      return 0;
    }
    if (orderLotVariant === 'MINI'
        && data.mini_interaday !== undefined
        && data.mini_interaday !== null
        && String(data.mini_interaday) !== '') {
      const miniRate = Number(data.mini_interaday);
      if (Number.isFinite(miniRate)) {
        return miniRate;
      }
    }
    const megaRate = Number(data.interaday || 0);
    return Number.isFinite(megaRate) ? megaRate : 0;
  };

  const toggleDepth = () => {
    setIsDepthExpanded(!isDepthExpanded);
  };

  const prevLotsRef = useRef(lots);
  const prevQuantityRef = useRef(quantity);

  useEffect(() => {
    if (isOpen) {
      const openAsMini = String(stock.lotVariant || '').toUpperCase() === 'MINI' && canTradeMini;
      setLotVariant(openAsMini ? 'MINI' : 'MEGA');
      setIsDepthExpanded(true);
      const miniSize = Number(miniScripts[scriptKey]?.mini_lot_size || 0);
      const initialLotSize = openAsMini && miniSize > 0
        ? miniSize
        : (stock.lotsize || stock.lotSize || 1);
      setQuantity(Math.max(initialLotSize, 1));
      setLots(1);
      prevLotsRef.current = 1;
      prevQuantityRef.current = Math.max(initialLotSize, 1);
    }
  }, [isOpen, stock.lotsize, stock.lotSize, stock.instrumentIdentifier, stock.lotVariant, canTradeMini]);

  useEffect(() => {
    if (!canTradeMini && lotVariant === 'MINI') {
      setLotVariant('MEGA');
    }
  }, [canTradeMini, lotVariant]);

  useEffect(() => {
    const newQuantity = lots * effectiveLotSize;
    if (prevLotsRef.current !== lots) {
      setQuantity(newQuantity);
      prevLotsRef.current = lots;
      prevQuantityRef.current = newQuantity;
    }
  }, [lots, effectiveLotSize]);

  useEffect(() => {
    const newLots = quantity / effectiveLotSize;
    if (prevQuantityRef.current !== quantity) {
      setLots(newLots);
      prevQuantityRef.current = quantity;
      prevLotsRef.current = newLots;
    }
  }, [quantity, effectiveLotSize]);

  const incrementLots = () => {
    // setLots(lots + .1);
    setLots(lots + 1);
  };

  const decrementLots = () => {
    // const lotSize = stock.lotsize || 1;
    // const minLots = 1 / lotSize;
    // const nextLots = lots - 0.1;
    // if (nextLots >= minLots) {
    //   setLots(nextLots);
    // }
    if (lots > 1) {
      setLots(lots - 1);
    }
  };

  const incrementQuantity = () => {
    // // var lotsize = stock.lotsize ? stock.lotsize : 1;
    // // var new_quantity = Number(quantity) + Number(lotsize);
    // // setQuantity(new_quantity);
    // if(stock.exchange != "OPTSTK"){
    //   setQuantity(Number(quantity)+1);
    // }
          // if(stock.exchange == "FUTSTK"){
          //   setQuantity(Number(quantity)+1);
          // }
    // if(stock.exchange != "OPTSTK"){
      setQuantity(Number(quantity)+1);
    // }
  };

  const decrementQuantity = () => {
    // if(stock.exchange != "OPTSTK"){
    //   if(quantity > 1){
    //     setQuantity(Number(quantity)-1);
    //   }
    // }
            // if(stock.exchange == "FUTSTK"){
            //   if(quantity > 1){
            //     setQuantity(Number(quantity)-1);
            //   }
            // }
    // if(stock.exchange != "OPTSTK"){
      if(quantity > 1){
        setQuantity(Number(quantity)-1);
      }
    // }
  };

  const handleOrderTypeToggle = () => {
    setIsLimitOrder(!isLimitOrder);
    //alert("Limit Order: "+!isLimitOrder)
  };

  // Calculate the change percentage
  const _Bid = stock.buyPrice = stock.live.Bid!=undefined ? stock.live.Bid : stock.buyPrice;
  const _Ask = stock.sellPrice = stock.live.Ask!=undefined ? stock.live.Ask : stock.sellPrice;
  const _Open = stock.live.Open!=undefined ? stock.live.Open : stock.open;
  const _High = stock.live.High!=undefined ? stock.live.High : stock.high;
  const _Low = stock.live.Low!=undefined ? stock.live.Low : stock.low;
  const _Prev_Close = stock.live.Prev_Close!=undefined ? stock.live.Prev_Close : stock.close;
  const _LTP = stock.live.LTP!=undefined ? stock.live.LTP : stock.lastTradedPrice;

  const Change_per = ((_LTP - _Prev_Close) / _Prev_Close * 100).toFixed(2);
  const Change = (_LTP - _Prev_Close).toFixed(2);

  const buyDepth = stock.live.depth?.buy || [];
  const sellDepth = stock.live.depth?.sell || [];

  const handleBackdropClick = (e) => {
    if (e.target.classList.contains('backdrop')) {
      onClose();
    }
  };

  const handleOrderTypeChange = (event, newOrderType) => {
    setOrderType(newOrderType);
  };

  const handleQuantityChange = (event) => {
    setQuantity(parseInt(event.target.value) || 0);
                  // const parsedValue = parseInt(event.target.value, 10);
                  // setQuantity(Math.max(Number.isNaN(parsedValue) ? 1 : parsedValue, 1)); // Update quantity while enforcing minimum 1
    // const value = event.target.value;
    // console.log("I am working");
    // if (!isNaN(value) && value >= 0) {
    //   setQuantity(value);
    // }
    // else{
    //   setQuantity('');
    // }
  };

  const createOrder_old = (orderType) => {
    const orderDetails = {
      stock,
      quantity,
      price: isLimitOrder === true ? price : (orderType === 'buy' ? _Ask : _Bid),
      orderType
    };
    console.log(orderDetails); // Replace this with your order creation logic
  };

  const watchlistLimitPriceError = (side) => {
    if (price == _LTP) {
      return 'Order price should not be equal to LTP.';
    }
    if (Number(price) < Number(stock.lower_circuit) || Number(price) > Number(stock.upper_circuit)) {
      return 'Order price is beyond daily price range.';
    }
    if (Number(side) === 1) {
      if (price >= stock.buyPrice) {
        return 'Order price must be less than bid price.';
      }
    } else if (price <= stock.sellPrice) {
      return 'Order price should be greater than ask price.';
    }
    return '';
  };

  const placeWatchlistNetOrder = async (orderType, position, plan) => {
    setDisable(true);
    if (plan.rejectLimitOverQty) {
      alert('For a limit order, quantity cannot be more than the open quantity.');
      setDisable(false);
      return;
    }
    if (Number(quantity) < 1) {
      alert('Quantity should be at least 1.');
      setDisable(false);
      return;
    }
    if (isLimitOrder) {
      if (stock.buyPrice <= 0 || stock.sellPrice <= 0) {
        alert('Price not updated, please refresh');
        setDisable(false);
        return;
      }
      const limitError = watchlistLimitPriceError(orderType);
      if (limitError) {
        alert(limitError);
        setDisable(false);
        submitRejectionLog(JSON.stringify({
          clientId: userDetails.clientId,
          parent_id: userDetails.parentId,
          stockId: stock.instrumentIdentifier,
          stockName: '"' + stock.dispname + '"',
          stockExpiry: '"' + stock.expiry + '"',
          orderTransactionType: 2,
          orderExecutionType: 2,
          buy_sell: orderType,
          lot: lots,
          quantity: quantity,
          remark: '"' + limitError + '"',
          orderPrice: price,
          lotVariant: orderLotVariant,
        }));
        return;
      }
    }

    const squareOff = Number(userDetails.onlyPositionSquareOff) === 1
      || Number(userDetails.masterOnlyPositionSquareOff) === 1;
    const extraQty = squareOff ? 0 : Number(plan.extraQty);
    const closeQty = Number(plan.closeQty);
    const lotSize = Number(position.usedLotSize || position.lotSize || position.stockLotSize || effectiveLotSize) || effectiveLotSize;
    const entryPrice = Number(orderType) === 1 ? Number(position.avgSellPrice || 0) : Number(position.avgBuyPrice || 0);
    const closeStock = {
      ...stock,
      dispName: stock.dispname,
      stockId: position.stockId,
      stockName: position.stockName || stock.dispname,
      instrumentIdentifier: position.stockId || stock.instrumentIdentifier,
      exchange: position.exchange || stock.exchange,
      expiry: position.expiry || stock.expiry,
      usedLotSize: lotSize,
      stockLotSize: lotSize,
      lotSize: lotSize,
      lotsize: lotSize,
      avgBuyPrice: position.avgBuyPrice,
      avgSellPrice: position.avgSellPrice,
      positionId: position.positionId,
      autoCutDate: position.autoCutDate || stock.autoCutDate,
      lotVariant: orderLotVariant,
    };

    try {
      const closeResponse = await mediatorAppFetch({
        method: 'POST',
        body: JSON.stringify({
          stock: closeStock,
          orderTypeBuySell: orderType,
          orderExecutionType: isLimitOrder ? 2 : 1,
          lots: lotSize > 0 ? closeQty / lotSize : closeQty,
          quantity: closeQty,
          lotVariant: orderLotVariant,
          orderTransactionType: 2,
          market_type: stock.exchange + '_intraday',
          limitSlPrice: price,
          entryPrice: entryPrice,
          orderDevice: 1,
          ip: process.env.REACT_APP_USER_IP,
        }),
      });
      const closeData = await closeResponse.json();
      if (!closeData.success) {
        setDisable(false);
        alert(closeData.message || 'Close order failed.');
        return;
      }
      const cancelNote = pendingOrdersCancelledNote(closeData.message);

      if (extraQty > 0.0001) {
        const openStock = { ...stock, dispName: stock.dispname };
        const openResponse = await mediatorAppFetch({
          method: 'POST',
          body: JSON.stringify({
            stock: openStock,
            orderTypeBuySell: orderType,
            orderExecutionType: isLimitOrder ? 2 : 1,
            lots: effectiveLotSize > 0 ? extraQty / effectiveLotSize : extraQty,
            lotVariant: orderLotVariant,
            orderTransactionType: 1,
            market_type: stock.exchange + '_intraday',
            limitSlPrice: price,
            orderDevice: 1,
            ip: process.env.REACT_APP_USER_IP,
          }),
        });
        const openData = await openResponse.json();
        setDisable(false);
        refreshTradesAndPortfolio('both');
        if (!openData.success) {
          alert('Position closed. The extra quantity was not opened. ' + (openData.message || '') + cancelNote);
          return;
        }
        const sideLabel = Number(orderType) === 1 ? 'Buy' : 'Sell';
        setSuccessAlert({
          show: true,
          title: 'Order Successful',
          message: (
            <>
              {sideLabel} closed {closeQty} and opened {extraQty} of {orderScriptLabel(stock.dispname)}.
              {cancelNote}
            </>
          ),
        });
        return;
      }

      setLots(1);
      setDisable(false);
      refreshTradesAndPortfolio('both');
      const sideLabel = Number(orderType) === 1 ? 'Buy' : 'Sell';
      const closedNote = squareOff && Number(plan.extraQty) > 0.0001
        ? ' Square-off only, so the quantity above the open position was not opened.'
        : '';
      setSuccessAlert({
        show: true,
        title: isLimitOrder ? 'Limit Order Successful' : 'Order Successful',
        message: (
          <>
            {sideLabel} {closeQty} of {orderScriptLabel(stock.dispname)}.
            {closedNote}
            {cancelNote}
          </>
        ),
      });
    } catch (error) {
      console.error(error);
      setDisable(false);
      alert('Order failed. Please try again.');
    }
  };

  const createOrder = async (orderType) => {
    playTradeSound(orderType === 1 ? 'buy' : 'sell');
    const position = findWatchlistPosition(stock, netPositions, orderLotVariant);
    const plan = planOppositeWatchlistOrder(position, orderType, quantity, isLimitOrder);
    if (plan) {
      return placeWatchlistNetOrder(orderType, position, plan);
    }
    setDisable(true);

    if (userDetails.marginAvailable <= 0) {
      alert("You don't have sufficient margin.");
      setDisable(false);

      var data = JSON.stringify({
        clientId: userDetails.clientId,
        parent_id: userDetails.parentId,
        parent_id: userDetails.parentId,
        stockId: stock.instrumentIdentifier,
        stockName: '"'+stock.dispname+'"',
        stockExpiry: '"'+stock.expiry+'"',
        orderTransactionType: 1,
        orderExecutionType: isLimitOrder ? 2 : 1,
        buy_sell: orderType,
        lot: lots,
        quantity: quantity,
        remark: '"You do not have balance."',
        orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice)
      });

      submitRejectionLog(data);

      return;
    }

    if (quantity < 1) {
      alert("Quantity should be at least 1.");
      setDisable(false);

      var data = JSON.stringify({
        clientId: userDetails.clientId,
        parent_id: userDetails.parentId,
        stockId: stock.instrumentIdentifier,
        stockName: '"'+stock.dispname+'"',
        stockExpiry: '"'+stock.expiry+'"',
        orderTransactionType: 1,
        orderExecutionType: isLimitOrder ? 2 : 1,
        buy_sell: orderType,
        lot: lots,
        quantity: quantity,
        remark: '"Quantity should be at least 1."',
        orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice)
      });

      submitRejectionLog(data);

      return;
    }

    // if(stock.exchange === "OPTSTK"){
    //   if (typeof lots === 'number' && lots % 1 !== 0) {
    //     // console.log("The value is in points (decimal).");

    //     alert("Lots should not be in points.");
    //     setDisable(false);

    //     var data = JSON.stringify({
    //       clientId: userDetails.clientId,
    //       parent_id: userDetails.parentId,
    //       stockId: stock.instrumentIdentifier,
    //       stockName: '"'+stock.dispname+'"',
    //       stockExpiry: '"'+stock.expiry+'"',
    //       orderTransactionType: 1,
    //       orderExecutionType: isLimitOrder ? 2 : 1,
    //       buy_sell: orderType,
    //       lot: lots,
    //       quantity: quantity,
    //       remark: '"Lots should not be in points."',
    //       orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice)
    //     });

    //     submitRejectionLog(data);

    //     return;

    //   }
    // }

    if (orderType === 2 && stock.exchange === "OPTSTK") {
      if (Number(userDetails.availableMargin) < Number(userDetails.OPTSTK_balance)) {
        alert(`You don't have sufficient margin to sell.\nMinimum ${userDetails.OPTSTK_balance} is required.`);
        setDisable(false);

        var data = JSON.stringify({
          clientId: userDetails.clientId,
          parent_id: userDetails.parentId,
          stockId: stock.instrumentIdentifier,
          stockName: '"'+stock.dispname+'"',
          stockExpiry: '"'+stock.expiry+'"',
          orderTransactionType: 1,
          orderExecutionType: isLimitOrder ? 2 : 1,
          buy_sell: orderType,
          lot: lots,
          quantity: quantity,
          remark: '"You don not have sufficient margin to sell."',
          orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice)
        });
  
        submitRejectionLog(data);

        return;
      }
    }

    // const nowTime = new Date();
    // const lastTradeTimeUnix = new Date(stock.lastTradeTime * 1000);
    // const timeDiffInSecond = Math.ceil((nowTime - lastTradeTimeUnix) / 1000);

    // if (stock.autoCutDate <= new Date().toISOString().split('T')[0]) {
    //   alert("You can't trade on autocut date.\nOpened position can be closed from portfolio.");
    //   var data = JSON.stringify({
    //     clientId: userDetails.clientId,
    //     parent_id: userDetails.parentId,
    //     stockId: stock.instrumentIdentifier,
    //     stockName: '"'+stock.dispname+'"',
    //     stockExpiry: '"'+stock.expiry+'"',
    //     orderTransactionType: 1,
    //     orderExecutionType: isLimitOrder ? 2 : 1,
    //     buy_sell: orderType,
    //     lot: lots,
    //     quantity: quantity,
    //     remark: '"You can not trade on autocut date.\nOpened position can be closed from portfolio."',
    //     orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice)
    //   });

    //   submitRejectionLog(data);
    //   return;
    // }

    if (stock.buyPrice <= 0 || stock.sellPrice <= 0) {
      alert("Price not updated, please refresh");
      setDisable(false);

      var data = JSON.stringify({
        clientId: userDetails.clientId,
        parent_id: userDetails.parentId,
        stockId: stock.instrumentIdentifier,
        stockName: '"'+stock.dispname+'"',
        stockExpiry: '"'+stock.expiry+'"',
        orderTransactionType: 1,
        orderExecutionType: isLimitOrder ? 2 : 1,
        buy_sell: orderType,
        lot: lots,
        quantity: quantity,
        remark: '"Price not updated, please refresh."',
        orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice)
      });

      submitRejectionLog(data);
      
      return;
    }

    if (userDetails.availableMargin <= 0) {
      alert("You do not have sufficient margin.");
      setDisable(false);

      var data = JSON.stringify({
        clientId: userDetails.clientId,
        parent_id: userDetails.parentId,
        stockId: stock.instrumentIdentifier,
        stockName: '"'+stock.dispname+'"',
        stockExpiry: '"'+stock.expiry+'"',
        orderTransactionType: 1,
        orderExecutionType: isLimitOrder ? 2 : 1,
        buy_sell: orderType,
        lot: lots,
        quantity: quantity,
        remark: '"You do not have sufficient margin."',
        orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice)
      });

      submitRejectionLog(data);
      return;
    }

    let orderAmount = 0;

    if (isLimitOrder) {

      if(price == _LTP){
          alert("Order price should not be equal to LTP.");
          setDisable(false);

          var data = JSON.stringify({
            clientId: userDetails.clientId,
            parent_id: userDetails.parentId,
            stockId: stock.instrumentIdentifier,
            stockName: '"'+stock.dispname+'"',
            stockExpiry: '"'+stock.expiry+'"',
            orderTransactionType: 1,
            orderExecutionType: 2,
            buy_sell: orderType,
            lot: lots,
            quantity: quantity,
            remark: '"Order price should not be equal to LTP."',
            orderPrice: price
          });
    
          submitRejectionLog(data);

          return;
      }

      orderAmount = quantity * price;

      if(Number(price) < Number(stock.lower_circuit) || Number(price) > Number(stock.upper_circuit)) {
          alert("Order price is beyond daily price range.");
          
          setDisable(false);

          var data = JSON.stringify({
            clientId: userDetails.clientId,
            parent_id: userDetails.parentId,
            stockId: stock.instrumentIdentifier,
            stockName: '"'+stock.dispname+'"',
            stockExpiry: '"'+stock.expiry+'"',
            orderTransactionType: 1,
            orderExecutionType: 2,
            buy_sell: orderType,
            lot: lots,
            quantity: quantity,
            remark: '"Order price is beyond daily price range"',
            orderPrice: price
          });

          submitRejectionLog(data);

          return;
      }

      if (orderType === 1) {
        if (price >= stock.buyPrice) {
          alert("Order price must be less than bid price.");
          setDisable(false);

          var data = JSON.stringify({
            clientId: userDetails.clientId,
            parent_id: userDetails.parentId,
            stockId: stock.instrumentIdentifier,
            stockName: '"'+stock.dispname+'"',
            stockExpiry: '"'+stock.expiry+'"',
            orderTransactionType: 1,
            orderExecutionType: 2,
            buy_sell: orderType,
            lot: lots,
            quantity: quantity,
            remark: '"Order price must be less than bid price"',
            orderPrice: price
          });

          submitRejectionLog(data);

          return;
        }
      } else {
        if (price <= stock.sellPrice) {
          alert("Order price should be greater than ask price.");
          setDisable(false);

          var data = JSON.stringify({
            clientId: userDetails.clientId,
            parent_id: userDetails.parentId,
            stockId: stock.instrumentIdentifier,
            stockName: '"'+stock.dispname+'"',
            stockExpiry: '"'+stock.expiry+'"',
            orderTransactionType: 1,
            orderExecutionType: 2,
            buy_sell: orderType,
            lot: lots,
            quantity: quantity,
            remark: '"Order price should be greater than ask price"',
            orderPrice: price
          });

          submitRejectionLog(data);

          return;
        }
      }
    }

    if (orderType === 2) 
    {
        if (stock.buyPrice <= 0) 
        {
            alert("Price not updated, please refresh");
            setDisable(false);

            var data = JSON.stringify({
              clientId: userDetails.clientId,
              parent_id: userDetails.parentId,
              stockId: stock.instrumentIdentifier,
              stockName: '"'+stock.dispname+'"',
              stockExpiry: '"'+stock.expiry+'"',
              orderTransactionType: 1,
              orderExecutionType: isLimitOrder ? 2 : 1,
              buy_sell: orderType,
              lot: lots,
              quantity: quantity,
              remark: '"Price not updated, please refresh."',
              orderPrice: stock.buyPrice
            });
  
            submitRejectionLog(data);

            return;
        }
        orderAmount = quantity * stock.buyPrice;
    } 
    else if (orderType === 1) {
      if (stock.sellPrice <= 0) {
          alert("Price not updated, please refresh");
          setDisable(false);

          var data = JSON.stringify({
            clientId: userDetails.clientId,
            parent_id: userDetails.parentId,
            stockId: stock.instrumentIdentifier,
            stockName: '"'+stock.dispname+'"',
            stockExpiry: '"'+stock.expiry+'"',
            orderTransactionType: 1,
            orderExecutionType: isLimitOrder ? 2 : 1,
            buy_sell: orderType,
            lot: lots,
            quantity: quantity,
            remark: '"Price not updated, please refresh."',
            orderPrice: stock.sellPrice
          });

          submitRejectionLog(data);

          return;
      }
      orderAmount = quantity * stock.sellPrice;
    }

    let requiredMargin = 0;

    if (stock.exchange === "FUTCOM") {
      if(userDetails.FUTCOM_margin_type === "script_wise")
      {
        console.log(stock.scriptName);
        console.log(userDetails);

        // Parse the script wise margin data and get interday value
        const scriptMarginData = JSON.parse(userDetails.script_wise_margin[stock.scriptName]);
        const scriptInterdayValue = scriptMarginData.interaday;
        console.log("Script interday value:", scriptInterdayValue);
        
        // Calculate required margin using script wise interday value
        requiredMargin = scriptInterdayValue;
        // return;
      }
      else{
        requiredMargin = orderAmount / userDetails.FUTCOM_intraday;
      }
    } else if (stock.exchange === "FUTSTK") {
      const futstkScripts = ['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'MIDCPNIFTY'];
      if(userDetails.FUTSTK_margin_type === "script_wise" && futstkScripts.includes(stock.scriptName))
      {
        const scriptMarginData = JSON.parse(userDetails.script_wise_margin[stock.scriptName]);
        const scriptInterdayValue = scriptMarginData.interaday;
        requiredMargin = scriptInterdayValue;
      }
      else{
        requiredMargin = orderAmount / userDetails.FUTSTK_intraday;
      }
    } else if (stock.exchange === "OPTSTK") {
      requiredMargin = orderAmount / userDetails.OPTSTK_intraday;
    }

    if (orderType === 2 && stock.exchange === "OPTSTK") {
      requiredMargin = lots * userDetails.OPTSTK_balance;
    }

    if (requiredMargin > userDetails.availableMargin) {
      alert(`Can't place order.\nRequired margin: ${Number(requiredMargin).toFixed(2)}, current margin: ${Number(userDetails.availableMargin).toFixed(2)}`);
      setDisable(false);

      var data = JSON.stringify({
        clientId: userDetails.clientId,
        parent_id: userDetails.parentId,
        stockId: stock.instrumentIdentifier,
        stockName: '"'+stock.dispname+'"',
        stockExpiry: '"'+stock.expiry+'"',
        orderTransactionType: 1,
        orderExecutionType: isLimitOrder ? 2 : 1,
        buy_sell: orderType,
        lot: lots,
        quantity: quantity,
        remark: '"Can not place order. Required margin: '+Number(requiredMargin).toFixed(2)+', current margin: '+Number(userDetails.availableMargin).toFixed(2)+'"',
        orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice)
      });

      submitRejectionLog(data);

      return;
    }

    if (lots <= 0 || lots <= 0.0) {
      alert("Please enter lot quantity.");
      setDisable(false);

      var data = JSON.stringify({
        clientId: userDetails.clientId,
        parent_id: userDetails.parentId,
        stockId: stock.instrumentIdentifier,
        stockName: '"'+stock.dispname+'"',
        stockExpiry: '"'+stock.expiry+'"',
        orderTransactionType: 1,
        orderExecutionType: isLimitOrder ? 2 : 1,
        buy_sell: orderType,
        lot: lots,
        quantity: quantity,
        remark: '"Please enter lot quantity."',
        orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice)
      });

      submitRejectionLog(data);

      return;
    }

    stock.dispName = stock.dispname;

    try {
      const response = await mediatorAppFetch({
        method: 'POST',
        body: JSON.stringify({
          stock,
          orderTypeBuySell: orderType,
          orderExecutionType: isLimitOrder ? 2 : 1,
          lots: lots,
          lotVariant: orderLotVariant,
          orderTransactionType: 1,
          market_type: `${stock.exchange}_intraday`,
          limitSlPrice: price,
          orderDevice: 1,
          ip: process.env.REACT_APP_USER_IP
        }),
      });
      const data = await response.json();

      if (!data.success) {
        
        setDisable(false);
        
        var msg = data.message;
        
        if (data.message === "open position") {
          msg = "Please close position from portfolio.";
        } else if (data.message === "open pending position") {
          msg = "You have a pending limit order on this script. You can cancel it from Trades."
        } else if (data.message.includes("Blocked")) {
          msg = "Trading not allowed on this script.";
        } else {
          msg = data.message;
          //alert(data.message);
        }


        showToast(3, msg, "Order Failed");
        
        var dataa = JSON.stringify({
          clientId: userDetails.clientId,
          parent_id: userDetails.parentId,
          stockId: stock.instrumentIdentifier,
          stockName: '"'+stock.dispname+'"',
          stockExpiry: '"'+stock.expiry+'"',
          orderTransactionType: 1,
          orderExecutionType: isLimitOrder ? 2 : 1,
          buy_sell: orderType,
          lot: lots,
          quantity: quantity,
          remark: '"'+msg+'"',
          orderPrice: isLimitOrder ? price : (orderType==1 ? stock.sellPrice : stock.buyPrice)
        });
  
        submitRejectionLog(dataa);
      } 
      else 
      {
        setLots(1);
        setDisable(false);

        var b_s = orderType == 1 ? 'Buy' : 'Sell';
        refreshTradesAndPortfolio('both');
        // Custom success alert (works on all devices including iPhone); sheet closes only after user taps OK
        setSuccessAlert({
          show: true,
          title: isLimitOrder ? 'Limit Order Successful' : 'Order Successful',
          message: (
            <>
              {b_s} {Number(lots).toFixed(2)} Lot(s) of {orderScriptLabel(stock.dispName)} @ {Number(data.orderPrice).toFixed(2)}
            </>
          )
        });

      }
    } catch (error) {
      console.error("Error:", error);
      setDisable(false);
    }
  };

  const formatMoney = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '—';
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatPrice = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '—';
    return num.toFixed(2);
  };

  const getRequiredMargin = (side) => {
    try {
      const isScriptWise =
        (stock.exchange === 'FUTCOM' && userDetails.FUTCOM_margin_type === 'script_wise') ||
        (stock.exchange === 'FUTSTK' && userDetails.FUTCOM_margin_type === 'script_wise' &&
          ['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'MIDCPNIFTY'].includes(stock.scriptName));
      if (isScriptWise) {
        return Number(lots * scriptWiseIntradayRate());
      }
      if (stock.exchange === 'OPTSTK' && side === 'sell') {
        return Number(userDetails.OPTSTK_balance) * Number(lots);
      }
      const px = side === 'sell'
        ? Number(buyDepth[0]?.price ?? _Bid)
        : Number(sellDepth[0]?.price ?? _Ask);
      const lev = Number(stock.intraday) || 1;
      return (Number(quantity) * px) / lev;
    } catch (error) {
      return 0;
    }
  };

  const canSell =
    (userDetails.OPTSTK_sellAllow == 1 && stock.exchange === 'OPTSTK') ||
    stock.exchange !== 'OPTSTK';
  const changeUp = Number(Change) > 0;
  const exchangeLabel =
    stock.exchange === 'FUTCOM' ? 'MCX' :
    stock.exchange === 'FUTSTK' ? 'NSE FUT' :
    stock.exchange === 'OPTSTK' ? 'NSE OPT' :
    stock.exchange;

  return (
    <>
      <ToastContainer />
      {/* Custom success alert overlay - like SweetAlert, works on iOS */}
      {successAlert.show && (
        <animated.div
          className={`success-alert-overlay ${isDarkMode ? 'dark-theme' : 'light-theme'}`}
          style={{ opacity: successAlertSpring.opacity }}
          data-theme={isDarkMode ? 'dark' : 'light'}
          onClick={e => e.stopPropagation()}
        >
          <animated.div
            className="success-alert-box"
            style={{
              transform: successAlertSpring.scale.to(s => `scale(${s})`),
              opacity: successAlertSpring.opacity
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="success-alert-icon-wrap">
              <CheckCircleIcon className="success-alert-icon" />
            </div>
            <Typography variant="h6" className="success-alert-title">{successAlert.title}</Typography>
            <Typography className="success-alert-message">{successAlert.message}</Typography>
            <Button
              variant="contained"
              className="success-alert-btn"
              onClick={handleSuccessAlertClose}
              fullWidth
            >
              OK
            </Button>
          </animated.div>
        </animated.div>
      )}
      {isOpen && <div className="backdrop" onClick={handleBackdropClick} />}
      {isOpen && (
        <animated.div
          style={styles}
          className={`trade-sheet ${isDarkMode ? 'dark-theme' : 'light-theme'}`}
          data-theme={isDarkMode ? 'dark' : 'light'}
        >
          <div className="trade-sheet-handle" />

          <div className="trade-sheet-head">
            <div className="trade-sheet-script">
              <ScriptIcon name={stock.dispname} symbol={stock.scriptName || stock.symbol} size={36} />
              <div className="trade-sheet-identity">
                <div className="trade-sheet-name">
                  {displayScriptName(stock.dispname)}
                  {lotVariant === 'MINI' && <span className="lot-variant-mini">MINI</span>}
                </div>
                <div className="trade-sheet-meta">
                  <span className="trade-sheet-exch">{exchangeLabel}</span>
                  {stock.expiry && <span>{stock.expiry}</span>}
                </div>
              </div>
            </div>
            <div className="trade-sheet-ltp">
              <strong>₹{formatPrice(_LTP)}</strong>
              <span className={changeUp ? 'up' : 'down'}>
                {changeUp ? '+' : ''}{formatPrice(Change)} ({changeUp ? '+' : ''}{Change_per}%)
              </span>
            </div>
            {/* <button type="button" className="trade-sheet-close" onClick={onClose} aria-label="Close">
              <CloseIcon />
            </button> */}
          </div>

          <div className="trade-quote-panel">
            <div className="trade-quote-row">
              <div className="trade-quote bid">
                <span>Bid</span>
                <strong>{formatPrice(_Bid)}</strong>
              </div>
              <div className="trade-quote ask">
                <span>Ask</span>
                <strong>{formatPrice(_Ask)}</strong>
              </div>
            </div>
            <div className="trade-ohlc">
              {[
                ['Open', _Open],
                ['High', _High],
                ['Low', _Low],
                ['Close', _Prev_Close]
              ].map(([label, value]) => (
                <div key={label}>
                  <span>{label}</span>
                  <strong>{formatPrice(value)}</strong>
                </div>
              ))}
            </div>
            {stock.exchange !== 'OPTSTK' && (
              <div className="trade-range">
                Day range {stock.lower_circuit} – {stock.upper_circuit}
              </div>
            )}
          </div>

          {userDetails.showMarketDepth === 1 && buyDepth.length > 0 && (
            <div className="trade-depth">
              <button type="button" className="trade-depth-toggle" onClick={toggleDepth}>
                <span>Market Depth</span>
                {isDepthExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </button>
              {isDepthExpanded && (
                <div className="trade-depth-book">
                  <div>
                    <div className="trade-depth-col-head bid">
                      <span>Qty</span>
                      <span>Bid</span>
                    </div>
                    {buyDepth.map((row, index) => (
                      <div key={`b-${index}`} className="trade-depth-row">
                        <span>{row.quantity}</span>
                        <span className="bid">{formatPrice(row.price)}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="trade-depth-col-head ask">
                      <span>Ask</span>
                      <span>Qty</span>
                    </div>
                    {sellDepth.map((row, index) => (
                      <div key={`s-${index}`} className="trade-depth-row">
                        <span className="ask">{formatPrice(row.price)}</span>
                        <span>{row.quantity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="trade-controls">
            {stock.exchange !== 'OPTSTK' && (
              <div className="trade-type-row">
                <div className="trade-seg">
                  <button type="button" className={!isLimitOrder ? 'active' : ''} onClick={() => isLimitOrder && handleOrderTypeToggle()}>Market</button>
                  <button type="button" className={isLimitOrder ? 'active' : ''} onClick={() => !isLimitOrder && handleOrderTypeToggle()}>Limit</button>
                </div>
                {isLimitOrder && (
                  <input
                    type="number"
                    inputMode="decimal"
                    value={price}
                    placeholder="Limit price"
                    className="trade-limit-input"
                    onChange={(e) => setPrice(e.target.value)}
                  />
                )}
              </div>
            )}
            <div className="trade-qty">
              <div className="trade-qty-top">
                <div className={`trade-opened${openedPosition.side ? ` ${openedPosition.side.toLowerCase()}` : ''}`}>
                  <span>
                    Opened lots <b>{formatLotCount(openedPosition.lots)} ({formatLotCount(openedPosition.quantity)})</b>
                  </span>
                  {openedPosition.side && <span className="trade-opened-side">{openedPosition.side}</span>}
                </div>
                <span className="trade-lot-size">
                  {lotVariant === 'MINI' ? 'Mini lot' : 'Lot'} size {effectiveLotSize}
                </span>
              </div>
              <div className="trade-steppers">
                <div className="trade-stepper">
                  <label>Lots</label>
                  <div>
                    <button type="button" onClick={decrementLots}>−</button>
                    <input value={Number(lots).toFixed(2)} disabled readOnly />
                    <button type="button" onClick={incrementLots}>+</button>
                  </div>
                </div>
                <div className="trade-stepper">
                  <label>Quantity</label>
                  <div>
                    <button type="button" onClick={decrementQuantity}>−</button>
                    <input value={Number(quantity)} onChange={handleQuantityChange} inputMode="numeric" />
                    <button type="button" onClick={incrementQuantity}>+</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="trade-sheet-footer">
          <div className="trade-funds">
            <div className="trade-funds-bal">
              <span>Balance</span>
              <strong>₹{formatMoney(userDetails.availableMargin)}</strong>
            </div>
            <div className="trade-funds-req">
              {canSell && (
                <span>Sell margin <b>₹{formatMoney(getRequiredMargin('sell'))}</b></span>
              )}
              <span>Buy margin <b>₹{formatMoney(getRequiredMargin('buy'))}</b></span>
            </div>
          </div>

          <div className={`trade-actions${!canSell ? ' buy-only' : ''}`}>
            {canSell && (
              <button
                type="button"
                className="trade-btn sell"
                disabled={disableBtn}
                onClick={() => createOrder(2)}
              >
                <span>SELL</span>
                <small>@ {isLimitOrder && price ? formatPrice(price) : formatPrice(_Bid)}</small>
              </button>
            )}
            <button
              type="button"
              className="trade-btn buy"
              disabled={disableBtn}
              onClick={() => createOrder(1)}
            >
              <span>BUY</span>
              <small>@ {isLimitOrder && price ? formatPrice(price) : formatPrice(_Ask)}</small>
            </button>
          </div>
          </div>
        </animated.div>
      )}
    </>
  );
};

export default BottomSheet;
